import type { Rule } from './madrobot.schema';

export interface MadRobotPreset {
  key: string;
  label: string;
  description: string;
  rule: Rule;
}

const preset = (
  key: string,
  label: string,
  description: string,
  rule: Omit<Rule, 'id'>,
): MadRobotPreset => ({
  key,
  label,
  description,
  rule: { ...rule, id: '' },
});

// Curated chaos scenarios. The server fills in a UUID when the preset is
// added to the rule list and saved.
export const MAD_ROBOT_PRESETS: readonly MadRobotPreset[] = [
  preset(
    'slow-applications',
    'Slow Applications list (5s)',
    '5s latency on GET /applications/*',
    {
      name: 'Slow Applications list',
      pathPattern: '/applications{/*rest}',
      method: 'GET',
      mode: { kind: 'latency', delayMs: 5000 },
      probability: 1,
      enabled: true,
    },
  ),
  preset(
    'http-503-applications',
    '503 on Applications list',
    'Service Unavailable on GET /applications/*',
    {
      name: '503 on Applications',
      pathPattern: '/applications{/*rest}',
      method: 'GET',
      mode: { kind: 'httpError', status: 503 },
      probability: 1,
      enabled: true,
    },
  ),
  preset(
    'network-drop-images',
    'Drop Images socket',
    'Destroys the connection on GET /images/*',
    {
      name: 'Network drop on Images',
      pathPattern: '/images{/*rest}',
      method: 'GET',
      mode: { kind: 'networkDrop' },
      probability: 1,
      enabled: true,
    },
  ),
  preset(
    'invalid-json-accounts',
    'Malformed Accounts response',
    'Returns invalid JSON on GET /accounts',
    {
      name: 'Invalid JSON on Accounts',
      pathPattern: '/accounts',
      method: 'GET',
      mode: { kind: 'invalidJson' },
      probability: 1,
      enabled: true,
    },
  ),
  preset(
    'wrong-shape-system-services',
    'Wrong-shape System services',
    'Returns a string where the System services list is expected',
    {
      name: 'Wrong shape on System services',
      pathPattern: '/system/services',
      method: 'GET',
      mode: { kind: 'wrongShape', payload: 'not-the-expected-shape' },
      probability: 1,
      enabled: true,
    },
  ),
  preset(
    'flaky-everything',
    'Flaky everywhere (25% 500s)',
    'Random 500 on 25% of all GETs',
    {
      name: 'Flaky GETs',
      pathPattern: '{/*rest}',
      method: 'GET',
      mode: { kind: 'httpError', status: 500 },
      probability: 0.25,
      enabled: true,
    },
  ),
  preset(
    'slow-mutations',
    'Slow mutations (3s)',
    '3s latency on POST/PUT/PATCH everywhere',
    {
      name: 'Slow mutations',
      pathPattern: '{/*rest}',
      method: '*',
      mode: { kind: 'latency', delayMs: 3000 },
      probability: 1,
      enabled: true,
    },
  ),
  preset(
    '401-everywhere',
    '401 everywhere (session-expired flow)',
    'Exercises the auto-logout path triggered by 401 on any /service request',
    {
      name: '401 everywhere',
      pathPattern: '{/*rest}',
      method: '*',
      mode: { kind: 'httpError', status: 401 },
      probability: 1,
      enabled: true,
    },
  ),
];
